import React, { useEffect, useMemo } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useDatasets } from '../../shared/api/apiHooks';

import { FormattedMessageHelper } from '../../shared/helpers/internationalization';

import { useToggle } from 'react-use';
import { Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import { CategoriesModal } from './Categories/CategoriesModal';
import './Data.scss';
import { getMainDataPath } from './dataExplorerRouterPaths';
import { DatasetsFilterDefaultValues, DatasetsFiltersTransformers } from './DatasetFilters/datasetFilterConfig';
import { DatasetFilters } from './DatasetFilters/DatasetFilters';
import { DatasetsListTable } from './DatasetsListTable/DatasetsListTable';
import { useFilterPaginagion } from './Filters/auxiliaryFields';
import { useFormDataCategoryField } from './Filters/FilterFields/CategoryField';
import { filterFields } from './Filters/FilterFields/dataExplorerFieldsConfig';
import { useFilterForm, useFiltersChanged } from './Filters/useFilterForm';
import {
  CommonDataFieldsSelector,
  getDefaultCommonDataFilterValues,
  setRegionDelayValues,
} from './CommonDataFieldsSelector/CommonDataFieldsSelector';
import { useDataExplorerTrackCustomEvent } from './Filters/dataExplorerAnalytics';
import { useWatch } from 'react-hook-form';
import { DataExplorerLink } from './dataExplorerHistoryContext';
import { useSelector } from 'react-redux';
import { hasConsultantOrResearcherPermission } from '../../reducers/AuthenticationReducer/AuthenticationSelector';

function DatasetsList({ history }: RouteComponentProps) {
  const defaultValues = useMemo(() => ({ ...DatasetsFilterDefaultValues, ...getDefaultCommonDataFilterValues() }), []);

  const { control, reset, debouncedQueryObject } = useFilterForm({
    fieldTransformers: DatasetsFiltersTransformers,
    defaultValues: defaultValues,
  });

  const trackEvent = useDataExplorerTrackCustomEvent();
  const isPyramidMultiplierVisible = useSelector(hasConsultantOrResearcherPermission);

  const datasets = useDatasets({ queryObject: debouncedQueryObject });

  const filterPagination = useFilterPaginagion({ control, defaultValues });
  const isExtraFilterChanged = useFiltersChanged({
    control,
    fieldNames: [filterFields.valueScore.name, filterFields.alphaCount.name],
    defaultValues: defaultValues,
  });

  const [categoriesOpened, toggleCategoriesOpen] = useToggle(false);
  const { selectedCategory, onCategoryClick } = useFormDataCategoryField(control);
  const isSearchActive = (useWatch({ name: filterFields.search.name, control }) || '').trim() !== '';

  useEffect(() => {
    const controlValues = control.getValues();
    if (
      controlValues.region !== defaultValues.region ||
      controlValues.universe !== defaultValues.universe ||
      controlValues.delay !== defaultValues.delay
    ) {
      setRegionDelayValues({
        region: controlValues.region,
        delay: controlValues.delay.toString(),
        universe: controlValues.universe,
        instrumentType: defaultValues.instrumentType,
      });
    }
  }, [control, defaultValues]);

  return (
    <div className="data-main">
      <div className="data__header-container">
        <div className="data-breadcrumbs">
          <Breadcrumbs
            items={[
              {
                link: getMainDataPath(),
                name: <FormattedMessageHelper id="dataExplorer.title" />,
                LinkComponent: DataExplorerLink,
                onClickCapture: () => trackEvent('de_click_breadcrumb_main'),
              },
              {
                name: <FormattedMessageHelper id="dataExplorer.breadcrumbs.browseDatasets" />,
              },
            ]}
          />
        </div>
        <CommonDataFieldsSelector control={control} />
      </div>
      <DatasetFilters
        isExtraFilterChanged={isExtraFilterChanged}
        selectedCategory={selectedCategory}
        onCategoryModalOpen={() => {
          toggleCategoriesOpen();
        }}
        control={control}
        reset={reset}
      />

      <div>
        {categoriesOpened && (
          <CategoriesModal
            selectedCategory={selectedCategory}
            onCategoryClick={onCategoryClick}
            onClose={() => toggleCategoriesOpen(false)}
            control={control}
          />
        )}
      </div>

      <DatasetsListTable
        isSearchActive={isSearchActive}
        filterPagination={filterPagination}
        control={control}
        datasetQueryResult={datasets}
        isPyramidMultiplierVisible={isPyramidMultiplierVisible}
      />
    </div>
  );
}

export default withRouter(DatasetsList);
